import React, { useState, useEffect } from "react";
import {
  Autocomplete,
  Box,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  Button,
  TextField,
  Tooltip,
  Typography,
  Checkbox,
  FormControlLabel,
  FormGroup,
  FormHelperText,
  FormControl,
} from "@mui/material";

import AnalyticFilterList from "./AnalyticFilterList";

import { computeTypeNames } from "../../js/constants";
import config from "../../js/config";
import { getBearerString } from "../../js/jwt_token";

const nsToDateString = (nsStr) => {
  if (!nsStr || nsStr === 'null') return "";
  try {
    const date = new Date(Number(BigInt(nsStr) / BigInt(1e6)));
    // Adjust for timezone offset to prevent date from changing
    const userTimezoneOffset = date.getTimezoneOffset() * 60000;
    const adjustedDate = new Date(date.getTime() + userTimezoneOffset);
    const year = adjustedDate.getFullYear();
    const month = (adjustedDate.getMonth() + 1).toString().padStart(2, '0');
    const day = adjustedDate.getDate().toString().padStart(2, '0');
    return `${year}-${month}-${day}`;
  } catch (e) {
    console.error("Error converting nanosecond timestamp to date string:", e);
    return "";
  }
};


const LogsFilterDialog = ({ open, onClose, onSaveCallback, searchParams }) => {
  const filterDefaultValues = {
    compute_type: [],
    analytics: [],
    is_onboard: false,
    is_sim: false,
    launch_file_name: [],
    log_number: [],
    robot: [],
    run_id: [],
    features: [], // Note - Passed back filter object is {featureA: 1...}
    start_time_ns: "",
    end_time_ns: "",
  };

  const [distinctValues, setDistinctValues] = useState(null);
  const [filters, setFilters] = useState(filterDefaultValues);

  useEffect(() => {
    const getFiltersFromParams = (params) => {
      const newFilters = { ...filterDefaultValues };
      const processedKeys = new Set();

      // Handle array values first
      const arrayKeys = ['compute_type', 'launch_file_name', 'log_number', 'robot', 'run_id', 'features'];
      arrayKeys.forEach(key => {
        if (params.has(key)) {
          const values = params.getAll(key);
          newFilters[key] = values;
          processedKeys.add(key);
        }
      });

      // Handle all other single values
      params.forEach((value, key) => {
        if (processedKeys.has(key)) {
          return; // Skip keys that were already processed as arrays
        }

        if (Object.hasOwnProperty.call(newFilters, key)) {
          if (typeof newFilters[key] === 'boolean') {
            newFilters[key] = value === 'true';
          } else if (key === 'start_time_ns' || key === 'end_time_ns') {
            newFilters[key] = nsToDateString(value);
          } else {
            newFilters[key] = value;
          }
        }
      });

      // Convert numeric strings to numbers for multi-select fields
      ['compute_type', 'log_number'].forEach(key => {
        if (newFilters[key] && Array.isArray(newFilters[key])) {
          newFilters[key] = newFilters[key].map(v => typeof v === 'string' ? Number(v) : v).filter(v => !isNaN(v));
        }
      });

      return newFilters;
    };

    if (open) {
      const urlFilters = getFiltersFromParams(searchParams);
      // When the dialog opens, we load filters from the URL but preserve the
      // analytics filter from the current state, as it's too complex to be
      // stored in the URL and is treated as a session-only filter.
      setFilters(prevFilters => ({
        ...urlFilters,
        analytics: prevFilters.analytics,
      }));
    }
  }, [open, searchParams]);


  useEffect(() => {
    // Fetch distinct values only once
    const fetchDistinctValues = async () => {
      try {
        const response = await fetch(
          config.data_platform_api_url + "/logs/filter_options",
          {
            method: "GET",
            headers: {
              "Content-Type": "application/json",
              Authorization: getBearerString(),
            },
          },
        );
        const data = await response.json();
        console.log(data);
        setDistinctValues(data);
      } catch (error) {
        console.error("Failed to fetch distinct values:", error);
      }
    };

    if (open && !distinctValues) {
      fetchDistinctValues();
    }
  }, [open, distinctValues]);

  const handleFilterChange = (field, value) => {
    setFilters((prevFilters) => ({
      ...prevFilters,
      [field]: value,
    }));
  };

  const handleReset = () => {
    // Reset the Values
    setFilters(filterDefaultValues);

    // Send the Reset Values (empty dict)
    onSaveCallback({});
    return;
  };

  const onSave = () => {
    const ret_filters: any = {};

    // Add filters to ret_filters if they are not the default value
    Object.keys(filters).forEach((key) => {
      const value = filters[key];
      const defaultValue = filterDefaultValues[key];

      if (JSON.stringify(value) !== JSON.stringify(defaultValue)) {
        if (Array.isArray(value) && value.length === 0) {
          // Do not include empty arrays if they were empty by default
          return;
        }
        ret_filters[key] = value;
      }
    });

    // Return Empty Body if no filters were set
    if (Object.keys(ret_filters).length === 0) {
      onSaveCallback({});
      return;
    }

    // Exclude analytics from the URL params as it is too complex to serialize
    // into a URL-friendly format. It is handled as a session-only filter.
    delete ret_filters.analytics;

    // Transform Start and EndTime if there set -> epoch nanoseconds
    if (ret_filters.start_time_ns) {
      const [year, month, day] = ret_filters.start_time_ns.split('-').map(Number);
      // Create date in local timezone at the beginning of the day
      const startDate = new Date(year, month - 1, day, 0, 0, 0, 0);
      ret_filters.start_time_ns =
        (BigInt(startDate.getTime()) * BigInt(1e6)).toString();
    }
    if (ret_filters.end_time_ns) {
      const [year, month, day] = ret_filters.end_time_ns.split('-').map(Number);
      // Create date in local timezone at the end of the day
      const endDate = new Date(year, month - 1, day, 23, 59, 59, 999);
      ret_filters.end_time_ns = (BigInt(endDate.getTime()) * BigInt(1e6)).toString();
    }

    onSaveCallback(ret_filters);
    return;
  };

  return (
    <Dialog open={open} onClose={onClose} fullWidth>
      <DialogTitle>Set Log Filters</DialogTitle>
      <DialogContent>
        {distinctValues ? (
          <>
            <FormControl fullWidth margin="normal">
              <Autocomplete
                multiple
                options={distinctValues.compute_type.sort((a, b) =>
                  computeTypeNames[a].localeCompare(computeTypeNames[b]),
                )} // Inline sorting by compute type names
                value={filters.compute_type}
                onChange={(event, value) =>
                  handleFilterChange("compute_type", value)
                }
                getOptionLabel={(option) => computeTypeNames[option]} // Display compute type names instead of integers
                renderInput={(params) => (
                  <TextField
                    {...params}
                    label="Compute Type"
                    variant="outlined"
                    placeholder="Select or type..."
                  />
                )}
              />
              <FormHelperText>
                Select multiple compute types or start typing to filter the
                options.
              </FormHelperText>
            </FormControl>

            <Tooltip title="Checkboxes are assumed False - if left unchecked">
              <FormGroup>
                <FormControlLabel
                  control={
                    <Checkbox
                      checked={filters.is_onboard === true}
                      onChange={() =>
                        handleFilterChange("is_onboard", !filters.is_onboard)
                      }
                    />
                  }
                  label="Onboard?"
                />
                <FormControlLabel
                  control={
                    <Checkbox
                      checked={filters.is_sim === true}
                      onChange={() =>
                        handleFilterChange("is_sim", !filters.is_sim)
                      }
                    />
                  }
                  label="Sim?"
                />
              </FormGroup>
            </Tooltip>

            <FormControl fullWidth margin="normal">
              <Autocomplete
                multiple
                options={distinctValues.launch_file_name.sort((a, b) =>
                  a.localeCompare(b),
                )} // Inline sorting
                value={filters.launch_file_name}
                onChange={(event, value) =>
                  handleFilterChange("launch_file_name", value)
                }
                renderInput={(params) => (
                  <TextField
                    {...params}
                    label="Launch File Name"
                    variant="outlined"
                    placeholder="Select or type..."
                  />
                )}
              />
              <FormHelperText>
                Select multiple launch file names or start typing to filter the
                options.
              </FormHelperText>
            </FormControl>

            <FormControl fullWidth margin="normal">
              <Autocomplete
                multiple
                options={distinctValues.robot.sort((a, b) =>
                  a.localeCompare(b),
                )} // Inline sorting
                value={filters.robot}
                onChange={(event, value) => handleFilterChange("robot", value)}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    label="Robot"
                    variant="outlined"
                    placeholder="Select or type..."
                  />
                )}
              />
              <FormHelperText>
                Select multiple Robots or start typing to filter the options.
              </FormHelperText>
            </FormControl>

            <FormControl fullWidth margin="normal">
              <Autocomplete
                multiple
                options={distinctValues.run_id.sort((a, b) =>
                  a.localeCompare(b),
                )} // Inline sorting
                value={filters.run_id}
                onChange={(event, value) => handleFilterChange("run_id", value)}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    label="Run ID"
                    variant="outlined"
                    placeholder="Select or type..."
                  />
                )}
              />
              <FormHelperText>
                Select multiple Run IDs or start typing to filter the options.
              </FormHelperText>
            </FormControl>

            <FormControl fullWidth margin="normal">
              <Autocomplete
                multiple
                options={distinctValues.features.sort((a, b) =>
                  a.localeCompare(b),
                )} // Inline sorting
                value={filters.features}
                onChange={(event, value) =>
                  handleFilterChange("features", value)
                }
                renderInput={(params) => (
                  <TextField
                    {...params}
                    label="Log Features"
                    variant="outlined"
                    placeholder="Select or type..."
                  />
                )}
              />
              <FormHelperText>
                Select multiple Log Features or start typing to filter the
                options.
              </FormHelperText>
            </FormControl>

            <FormControl>
              <Box mb={2}>
                <Typography gutterBottom>Log Created Start</Typography>
                <TextField
                  label=""
                  type="date"
                  value={filters.start_time_ns}
                  onChange={(e) =>
                    handleFilterChange("start_time_ns", e.target.value)
                  }
                />
                <TextField
                  label=""
                  type="date"
                  value={filters.end_time_ns}
                  onChange={(e) =>
                    handleFilterChange("end_time_ns", e.target.value)
                  }
                />
              </Box>
            </FormControl>

            <FormControl fullWidth margin="normal">
              <AnalyticFilterList
                initialAnalyticFilterOptions={distinctValues.analytics}
                initialAnalyticFilters={filters.analytics}
                onChange={(field, value) => handleFilterChange(field, value)}
              />
            </FormControl>

            {/* Add similar controls for features, time_start_ns, end_time_ns as needed */}
          </>
        ) : (
          <DialogContentText>Loading filter options...</DialogContentText>
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={onSave} color="primary">
          Apply
        </Button>
        <Button onClick={handleReset} color="primary">
          Reset
        </Button>
        <Button onClick={onClose} color="primary">
          Cancel
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default LogsFilterDialog;

